// AttendanceAudit.js
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../AuthContext';
import {
  getStoredAttendance,
  saveStoredAttendance,
  getStoredCorrections,
  saveStoredCorrections,
  getStoredAuditLog,
  saveStoredAuditLog,
  getStoredSettings,
  saveStoredSettings
} from './attendanceSeed';
import {
  fetchAttendanceRecords,
  createAttendanceRecord,
  updateAttendanceRecord
} from '../../services/attendanceService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './AttendanceAudit.css';
// Material Icons
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import TuneIcon from '@mui/icons-material/Tune';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ShieldIcon from '@mui/icons-material/Shield';
import UpdateIcon from '@mui/icons-material/Update';
import SaveIcon from '@mui/icons-material/Save';

const mapStatusToBackend = (status) => {
  switch (status) {
    case 'Present': return 'PRESENT';
    case 'Absent': return 'ABSENT';
    case 'Late': return 'LATE';
    case 'Leave': return 'ON_LEAVE';
    default: return 'PRESENT';
  }
};

const mapStatusFromBackend = (status) => {
  switch (status) {
    case 'PRESENT': return 'Present';
    case 'ABSENT': return 'Absent';
    case 'LATE': return 'Late';
    case 'ON_LEAVE': return 'Leave';
    default: return status || 'Present';
  }
};

const AttendanceAudit = () => {
  const { user, userRole, token, orgId } = useAuth();

  // Active Tab: 'queue', 'audit', 'settings'
  const [activeTab, setActiveTab] = useState('queue');

  // Roster, corrections, and logs state
  const [corrections, setCorrections] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [settings, setSettings] = useState({
    approvalRequired: true,
    defaulterThreshold: 75,
    allowFutureDates: false,
    notifyAbsence: true,
    halfDayLateThreshold: 2
  });

  // Search filter for audit log
  const [auditSearch, setAuditSearch] = useState('');

  // Initial Load
  useEffect(() => {
    setCorrections(getStoredCorrections());
    setAuditLogs(getStoredAuditLog());
    setSettings(getStoredSettings());
  }, []);

  // Filter corrections
  const pendingRequests = useMemo(() => {
    return corrections.filter(c => c.status === 'Pending');
  }, [corrections]);

  const processedRequests = useMemo(() => {
    return corrections.filter(c => c.status !== 'Pending');
  }, [corrections]);

  // Filtered Audit logs
  const filteredAuditLogs = useMemo(() => {
    return auditLogs.filter(log => 
      log.studentName.toLowerCase().includes(auditSearch.toLowerCase()) ||
      log.changedBy.toLowerCase().includes(auditSearch.toLowerCase()) ||
      log.subjectName.toLowerCase().includes(auditSearch.toLowerCase()) ||
      log.className.toLowerCase().includes(auditSearch.toLowerCase())
    );
  }, [auditLogs, auditSearch]);

  // Approve Request logic
  const handleApprove = async (req) => {
    const adminUser = `${user?.displayName || user?.name || "Admin Ramesh"} (ADMIN)`;
    const timestamp = new Date().toISOString();

    // 1. Sync with backend API
    let dbUpdateSuccess = false;
    if (token) {
      try {
        const result = await fetchAttendanceRecords({
          filterType: 'user',
          id: req.studentId,
          startDate: req.date,
          endDate: req.date,
          pageSize: 10
        });
        const list = result?.items || result || [];
        const existingRecord = list.find(r => r.subject === req.subjectName || r.subject === req.subjectCode);

        if (existingRecord) {
          const payload = {
            ...existingRecord,
            attendanceStatus: mapStatusToBackend(req.newStatus),
            remarks: `Corrected: ${req.reason}${existingRecord.remarks ? ' | ' + existingRecord.remarks : ''}`,
            lastUpdatedBy: adminUser
          };
          await updateAttendanceRecord(existingRecord.attendanceId, payload);
        } else {
          const payload = {
            orgId: orgId || "org_demo_1",
            classId: req.classId,
            userId: req.studentId,
            academicYear: "2025-2026",
            attendanceDate: req.date,
            checkInTime: timestamp,
            userType: "STUDENT",
            attendanceStatus: mapStatusToBackend(req.newStatus),
            classOrLocation: req.className || "KV Center",
            subject: req.subjectName || req.subjectCode,
            remarks: `Corrected: ${req.reason}`,
            attendanceMethod: "MANUAL_ENTRY",
            createdBy: adminUser,
            lastUpdatedBy: adminUser
          };
          await createAttendanceRecord(payload);
        }
        dbUpdateSuccess = true;
      } catch (err) {
        console.error("Failed backend attendance modification during approval", err);
        toast.error("Database synchronization failed, updating local logs only.");
      }
    }

    // 2. Update corrections list
    const updatedCorrections = corrections.map(c => {
      if (c.id === req.id) {
        return {
          ...c,
          status: 'Approved',
          approvedBy: adminUser,
          approvedAt: timestamp
        };
      }
      return c;
    });

    // 3. Update the roster in egurukul:attendance
    const allAttendance = getStoredAttendance();
    const key = req.recordKey;
    const rosterRecord = allAttendance[key];

    if (rosterRecord) {
      const studentIndex = rosterRecord.students.findIndex(s => s.studentId === req.studentId);
      if (studentIndex !== -1) {
        // Mutate status and append explanation to remarks
        const oldStatus = rosterRecord.students[studentIndex].status;
        rosterRecord.students[studentIndex].status = req.newStatus;
        
        const existingRemark = rosterRecord.students[studentIndex].remark || '';
        rosterRecord.students[studentIndex].remark = existingRemark 
          ? `${existingRemark} (Corrected: ${req.reason})` 
          : `Corrected: ${req.reason}`;

        allAttendance[key] = rosterRecord;
        saveStoredAttendance(allAttendance);
      }
    } else {
      // If roster doesn't exist, create a draft entry to prevent data loss (unlikely but safe)
      console.warn(`Roster record for key ${key} not found during approval. Creating new seed slot.`);
      allAttendance[key] = {
        classId: req.classId,
        date: req.date,
        subjectCode: req.subjectCode,
        subjectName: req.subjectName,
        period: "General",
        status: "draft",
        markedBy: adminUser,
        markedAt: timestamp,
        students: [
          {
            studentId: req.studentId,
            studentName: req.studentName,
            admissionNumber: req.admissionNumber,
            status: req.newStatus,
            remark: `Corrected: ${req.reason}`
          }
        ]
      };
      saveStoredAttendance(allAttendance);
    }

    // 4. Create Audit Log
    const newAuditLog = {
      id: `aud_${Date.now()}`,
      timestamp: timestamp,
      classId: req.classId,
      className: req.className || "Grade 10-A",
      studentId: req.studentId,
      studentName: req.studentName,
      subjectName: req.subjectName,
      date: req.date,
      oldStatus: req.oldStatus,
      newStatus: req.newStatus,
      changedBy: adminUser,
      reason: `Approved correction request - ${req.reason}`
    };

    const updatedAuditLogs = [newAuditLog, ...auditLogs];

    // 5. Save everything to LocalStorage
    saveStoredCorrections(updatedCorrections);
    saveStoredAuditLog(updatedAuditLogs);

    // 6. Update local states
    setCorrections(updatedCorrections);
    setAuditLogs(updatedAuditLogs);

    // 7. Broadcast custom event to notify student/register modules
    window.dispatchEvent(new Event('attendance_data_updated'));

    if (dbUpdateSuccess) {
      toast.success(`Request approved! Attendance updated for ${req.studentName} on backend.`);
    } else {
      toast.success(`Request approved locally! Attendance updated for ${req.studentName}.`);
    }
  };

  // Reject Request logic
  const handleReject = (req) => {
    const adminUser = `${user?.displayName || user?.name || "Admin Ramesh"} (ADMIN)`;
    const timestamp = new Date().toISOString();

    // 1. Update corrections list
    const updatedCorrections = corrections.map(c => {
      if (c.id === req.id) {
        return {
          ...c,
          status: 'Rejected',
          approvedBy: adminUser,
          approvedAt: timestamp
        };
      }
      return c;
    });

    // 2. Save corrections list (Do NOT modify attendance or audit log on rejection)
    saveStoredCorrections(updatedCorrections);
    setCorrections(updatedCorrections);

    // 3. Broadcast custom event to notify other modules
    window.dispatchEvent(new Event('attendance_data_updated'));

    toast.warning(`Correction request rejected for ${req.studentName}.`);
  };

  // Save Settings configuration
  const handleSaveSettings = (e) => {
    e.preventDefault();
    saveStoredSettings(settings);
    
    // Broadcast setting changes
    window.dispatchEvent(new Event('attendance_data_updated'));
    toast.success("Attendance parameters and rules saved successfully.");
  };

  return (
    <div className="audit-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Header Panel */}
      <div className="audit-header">
        <div>
          <h1>Attendance Approvals & Audit</h1>
          <p>Authorize student correction requests, review immutable change logs, and adjust school policy rules.</p>
        </div>
        <div className="admin-status-badge">
          <SecurityIcon style={{ fontSize: '18px', marginRight: '6px' }} />
          <span>Admin Controls Active</span>
        </div>
      </div>

      {/* Statistics Cards Row */}
      <div className="stats-row">
        <div className="stat-card pending">
          <div className="pulse-circle-wrap">
            <span className={`pulse-dot ${pendingRequests.length > 0 ? 'active' : ''}`}></span>
            <HourglassEmptyIcon />
          </div>
          <div>
            <h3>{pendingRequests.length}</h3>
            <p>Pending Requests</p>
          </div>
        </div>

        <div className="stat-card approved">
          <CheckCircleIcon />
          <div>
            <h3>{processedRequests.filter(p => p.status === 'Approved').length}</h3>
            <p>Approved Changes</p>
          </div>
        </div>

        <div className="stat-card audit">
          <HistoryIcon />
          <div>
            <h3>{auditLogs.length}</h3>
            <p>Audit Log Entries</p>
          </div>
        </div>

        <div className="stat-card threshold">
          <TuneIcon />
          <div>
            <h3>{settings.defaulterThreshold}%</h3>
            <p>Defaulter Limit Threshold</p>
          </div>
        </div>
      </div>

      {/* Tabs Layout */}
      <div className="tabs-card">
        <div className="tabs-header">
          <button 
            className={`tab-btn ${activeTab === 'queue' ? 'active' : ''}`}
            onClick={() => setActiveTab('queue')}
          >
            <ShieldIcon style={{ fontSize: '16px', marginRight: '6px' }} />
            Correction Queue ({pendingRequests.length})
          </button>
          
          <button 
            className={`tab-btn ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            <UpdateIcon style={{ fontSize: '16px', marginRight: '6px' }} />
            Immutable Audit Trail
          </button>
          
          <button 
            className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <TuneIcon style={{ fontSize: '16px', marginRight: '6px' }} />
            Module Policy Settings
          </button>
        </div>

        <div className="tab-content-panel">
          {/* Correction Queue Tab */}
          {activeTab === 'queue' && (
            <div className="queue-tab-panel">
              <div className="section-title">
                <h2>Pending Adjustment Requests</h2>
                <p>Authorize student-submitted changes. Approving directly alters stateful KV Center rosters.</p>
              </div>

              {pendingRequests.length === 0 ? (
                <div className="empty-queue-state">
                  <CheckCircleIcon className="check-success-icon" style={{ fontSize: '56px' }} />
                  <h3>Approvals Queue is Clear!</h3>
                  <p>All submitted corrections have been successfully processed and cataloged.</p>
                </div>
              ) : (
                <div className="requests-table-wrapper">
                  <table className="audit-table">
                    <thead>
                      <tr>
                        <th>Date & Subject</th>
                        <th>Student & KV Center</th>
                        <th>Status Adjustment</th>
                        <th>Requested By & Reason</th>
                        <th style={{ width: '180px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingRequests.map((req) => (
                        <tr key={req.id}>
                          <td>
                            <div className="cell-date-subject">
                              <span className="date-text">{req.date}</span>
                              <span className="subject-text">{req.subjectName}</span>
                            </div>
                          </td>
                          <td>
                            <div className="cell-student-class">
                              <span className="student-name">{req.studentName}</span>
                              <span className="student-adm">{req.admissionNumber}</span>
                              <span className="class-tag">{req.className}</span>
                            </div>
                          </td>
                          <td>
                            <div className="cell-shift-display">
                              <span className="status-old">{req.oldStatus}</span>
                              <span className="arrow-shift">→</span>
                              <span className={`status-new ${req.newStatus}`}>{req.newStatus}</span>
                            </div>
                          </td>
                          <td>
                            <div className="cell-request-reason">
                              <span className="operator-text">By: <em>{req.requestedBy}</em></span>
                              <p className="reason-quote">"{req.reason}"</p>
                              <span className="timestamp-text">{new Date(req.requestedAt).toLocaleString()}</span>
                            </div>
                          </td>
                          <td>
                            <div className="action-buttons-group">
                              <button className="btn-action approve" onClick={() => handleApprove(req)}>
                                <CheckCircleIcon style={{ fontSize: '16px' }} /> Approve
                              </button>
                              <button className="btn-action reject" onClick={() => handleReject(req)}>
                                <CancelIcon style={{ fontSize: '16px' }} /> Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Processed requests log history */}
              {processedRequests.length > 0 && (
                <div className="processed-history-section">
                  <div className="section-title sub">
                    <h3>Recent Decisions Log ({processedRequests.length})</h3>
                  </div>
                  <div className="requests-table-wrapper">
                    <table className="audit-table processed">
                      <thead>
                        <tr>
                          <th>Date / Subject</th>
                          <th>Student</th>
                          <th>Shift</th>
                          <th>Approver Details</th>
                          <th>Decision Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {processedRequests.slice(0, 10).map((req) => (
                          <tr key={req.id}>
                            <td>
                              <div className="cell-date-subject">
                                <span className="date-text">{req.date}</span>
                                <span className="subject-text">{req.subjectName}</span>
                              </div>
                            </td>
                            <td>
                              <span className="student-name">{req.studentName}</span>
                            </td>
                            <td>
                              <span className="status-old">{req.oldStatus}</span> → <span className="status-new inline">{req.newStatus}</span>
                            </td>
                            <td>
                              <div className="cell-request-reason">
                                <span className="operator-text">Decided by: <em>{req.approvedBy}</em></span>
                                <span className="timestamp-text">{new Date(req.approvedAt).toLocaleString()}</span>
                              </div>
                            </td>
                            <td>
                              <span className={`decision-badge ${req.status.toLowerCase()}`}>
                                {req.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Immutable Audit Trail Tab */}
          {activeTab === 'audit' && (
            <div className="audit-tab-panel">
              <div className="panel-actions-bar">
                <div className="section-title">
                  <h2>Immutable System Audit Trail</h2>
                  <p>Immutable log of every manual attendance override. Provides accountability for compliance auditing.</p>
                </div>
                
                <div className="search-box-audit">
                  <span className="search-icon-pos"><SearchIcon /></span>
                  <input
                    type="text"
                    placeholder="Search by student, center, operator..."
                    className="search-input-audit"
                    value={auditSearch}
                    onChange={(e) => setAuditSearch(e.target.value)}
                  />
                </div>
              </div>

              {filteredAuditLogs.length === 0 ? (
                <div className="empty-queue-state">
                  <HistoryIcon style={{ fontSize: '56px', color: '#cbd5e1' }} />
                  <h3>No Audit Records Found</h3>
                  <p>Try modifying your search filter query.</p>
                </div>
              ) : (
                <div className="requests-table-wrapper">
                  <table className="audit-table log-trail">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Student & KV Center</th>
                        <th>Date & Subject</th>
                        <th>Status Change</th>
                        <th>Operator (Who)</th>
                        <th>Justification Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAuditLogs.map((log) => (
                        <tr key={log.id}>
                          <td style={{ whiteSpace: 'nowrap', fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td>
                            <div className="cell-student-class">
                              <span className="student-name">{log.studentName}</span>
                              <span className="class-tag">{log.className}</span>
                            </div>
                          </td>
                          <td>
                            <div className="cell-date-subject">
                              <span className="date-text">{log.date}</span>
                              <span className="subject-text">{log.subjectName}</span>
                            </div>
                          </td>
                          <td>
                            <div className="cell-shift-display inline">
                              <span className="status-old">{log.oldStatus}</span>
                              <span className="arrow-shift">→</span>
                              <span className={`status-new inline ${log.newStatus}`}>{log.newStatus}</span>
                            </div>
                          </td>
                          <td>
                            <span className="operator-name-highlight">{log.changedBy}</span>
                          </td>
                          <td>
                            <p className="reason-quote-inline">"{log.reason}"</p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Module Settings Tab */}
          {activeTab === 'settings' && (
            <div className="settings-tab-panel">
              <div className="section-title">
                <h2>Module Policy Configurations</h2>
                <p>Fine-tune school ERP business rules, thresholds, penalties, and automated parent alerts.</p>
              </div>

              <form onSubmit={handleSaveSettings} className="settings-form">
                <div className="settings-grid">
                  <div className="setting-card-item">
                    <div className="setting-info">
                      <h4>Admin Correction Approval Workflow</h4>
                      <p>Requires an administrator to explicitly review and authorize correction requests raised by student/faculty.</p>
                    </div>
                    <label className="switch-toggle">
                      <input 
                        type="checkbox"
                        checked={settings.approvalRequired}
                        onChange={(e) => setSettings(prev => ({ ...prev, approvalRequired: e.target.checked }))}
                      />
                      <span className="slider-toggle-round"></span>
                    </label>
                  </div>

                  <div className="setting-card-item">
                    <div className="setting-info">
                      <h4>Automated Parent Absence Alerts</h4>
                      <p>Trigger automated real-time SMS/Email hooks notifying parents when their student is finalized as Absent.</p>
                    </div>
                    <label className="switch-toggle">
                      <input 
                        type="checkbox"
                        checked={settings.notifyAbsence}
                        onChange={(e) => setSettings(prev => ({ ...prev, notifyAbsence: e.target.checked }))}
                      />
                      <span className="slider-toggle-round"></span>
                    </label>
                  </div>

                  <div className="setting-card-item">
                    <div className="setting-info">
                      <h4>Future Dates Entry Validation</h4>
                      <p>Allow teachers to pre-mark attendance calendars for dates beyond today's current server timestamp.</p>
                    </div>
                    <label className="switch-toggle">
                      <input 
                        type="checkbox"
                        checked={settings.allowFutureDates}
                        onChange={(e) => setSettings(prev => ({ ...prev, allowFutureDates: e.target.checked }))}
                      />
                      <span className="slider-toggle-round"></span>
                    </label>
                  </div>

                  <div className="setting-input-item">
                    <div className="setting-info">
                      <h4>Attendance Defaulter Limit (%)</h4>
                      <p>Center warning boundary below which students are flagged as chronic absentees (Standard: 75%).</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input 
                        type="range"
                        min="50"
                        max="95"
                        step="5"
                        className="slider-range-control"
                        value={settings.defaulterThreshold}
                        onChange={(e) => setSettings(prev => ({ ...prev, defaulterThreshold: parseInt(e.target.value) }))}
                      />
                      <span className="range-indicator-val">{settings.defaulterThreshold}%</span>
                    </div>
                  </div>

                  <div className="setting-input-item">
                    <div className="setting-info">
                      <h4>Half-Day Absence Penalty</h4>
                      <p>Number of cumulative late entries that triggers one half-day attendance absence penalty deduction.</p>
                    </div>
                    <select 
                      className="select-penalty-control"
                      value={settings.halfDayLateThreshold}
                      onChange={(e) => setSettings(prev => ({ ...prev, halfDayLateThreshold: parseInt(e.target.value) }))}
                    >
                      <option value="0">Disabled (Lates count as fully Present)</option>
                      <option value="2">2 Late marks = 0.5 Day Absent</option>
                      <option value="3">3 Late marks = 0.5 Day Absent</option>
                      <option value="4">4 Late marks = 0.5 Day Absent</option>
                    </select>
                  </div>
                </div>

                <div className="settings-footer-actions">
                  <button type="submit" className="btn-save-settings">
                    <SaveIcon style={{ fontSize: '18px', marginRight: '8px' }} />
                    Save Configuration Settings
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceAudit;
